import { useState, useEffect, useRef } from "react";
import { Plus, Loader2, Trash2, Upload, Eye, EyeOff, PackageOpen, Edit3, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLORS = {
  published: "bg-emerald-100 text-emerald-700 border-emerald-200",
  draft: "bg-amber-100 text-amber-700 border-amber-200",
  deleted: "bg-red-100 text-red-700 border-red-200",
};

export default function AdminMarketplace() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fileRef = useRef();
  const thumbRef = useRef();
  const [form, setForm] = useState({
    title: "", description: "", price_credits: "", total_contacts: ""
  });

  useEffect(() => { loadPackages(); }, []);

  const loadPackages = async () => {
    try {
      const res = await API.get("/marketplace/admin/packages");
      setPackages(res.data.packages || []);
    } catch { toast.error("Failed to load packages"); }
    finally { setLoading(false); }
  };

  const createPackage = async () => {
    if (!form.title || !form.price_credits || !form.total_contacts) {
      toast.error("Title, price and contact count are required");
      return;
    }
    if (!fileRef.current?.files?.[0]) {
      toast.error("Please upload an Excel file (.xlsx)");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("price_credits", form.price_credits);
      fd.append("total_contacts", form.total_contacts);
      fd.append("file", fileRef.current.files[0]);
      if (thumbRef.current?.files?.[0]) fd.append("thumbnail", thumbRef.current.files[0]);
      await API.post("/marketplace/admin/packages", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Package created as draft");
      setForm({ title: "", description: "", price_credits: "", total_contacts: "" });
      if (fileRef.current) fileRef.current.value = "";
      if (thumbRef.current) thumbRef.current.value = "";
      setShowForm(false);
      loadPackages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create package");
    } finally { setSaving(false); }
  };

  const toggleStatus = async (pkg) => {
    const newStatus = pkg.status === "published" ? "draft" : "published";
    try {
      await API.put(`/marketplace/admin/packages/${pkg._id}`, { status: newStatus });
      toast.success(`Package ${newStatus}`);
      loadPackages();
    } catch { toast.error("Failed to update status"); }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await API.delete(`/marketplace/admin/packages/${id}`);
      toast.success("Package deleted");
      loadPackages();
    } catch { toast.error("Failed to delete"); }
  };

  const startEdit = (pkg) => {
    setEditingId(pkg._id);
    setEditForm({ title: pkg.title, description: pkg.description, price_credits: pkg.price_credits, total_contacts: pkg.total_contacts });
  };

  const saveEdit = async (id) => {
    try {
      await API.put(`/marketplace/admin/packages/${id}`, editForm);
      toast.success("Package updated");
      setEditingId(null);
      loadPackages();
    } catch { toast.error("Failed to update"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Contact Marketplace</h1>
          <p className="text-sm text-slate-500 mt-1">Create and publish contact packages for customers to purchase</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm"
          data-testid="admin-create-package-btn"
        >
          <Plus size={15} className="mr-2" /> New Package
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 mb-4">Create New Contact Package</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Package Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Lagos Restaurant Owners" className="h-9 text-sm" data-testid="pkg-title-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Price (Credits) *</Label>
              <Input type="number" value={form.price_credits} onChange={e => setForm({ ...form, price_credits: e.target.value })}
                placeholder="e.g. 500" className="h-9 text-sm" data-testid="pkg-price-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Total Contacts *</Label>
              <Input type="number" value={form.total_contacts} onChange={e => setForm({ ...form, total_contacts: e.target.value })}
                placeholder="e.g. 1500" className="h-9 text-sm" data-testid="pkg-contacts-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Short description of contacts" className="h-9 text-sm" data-testid="pkg-description-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Excel File (.xlsx) *</Label>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="block w-full text-xs text-slate-700 border border-slate-300 rounded-md px-3 py-2 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#eef1f8] file:text-[#0c1e41] file:font-medium cursor-pointer" data-testid="pkg-file-input" />
              <p className="text-xs text-slate-400">Columns: phone (required), name, email, etc.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-xs">Thumbnail (optional)</Label>
              <input ref={thumbRef} type="file" accept="image/*" className="block w-full text-xs text-slate-700 border border-slate-300 rounded-md px-3 py-2 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-100 file:text-slate-600 file:font-medium cursor-pointer" data-testid="pkg-thumbnail-input" />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={createPackage} disabled={saving} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm text-sm" data-testid="pkg-save-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Upload size={14} className="mr-2" />}
              Create Package
            </Button>
            <Button variant="outline" onClick={() => setShowForm(false)} className="text-sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Packages list */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-[#0c1e41]" />
        </div>
      ) : packages.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <PackageOpen size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">No packages yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first contact package above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {packages.map(pkg => (
            <div key={pkg._id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`package-row-${pkg._id}`}>
              {editingId === pkg._id ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} placeholder="Title" className="h-8 text-xs col-span-2" />
                  <Input type="number" value={editForm.price_credits} onChange={e => setEditForm({ ...editForm, price_credits: e.target.value })} placeholder="Price" className="h-8 text-xs" />
                  <Input type="number" value={editForm.total_contacts} onChange={e => setEditForm({ ...editForm, total_contacts: e.target.value })} placeholder="Contacts" className="h-8 text-xs" />
                  <Input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" className="h-8 text-xs col-span-2" />
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(pkg._id)} className="flex items-center gap-1 text-xs px-2 py-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700" data-testid="save-edit-btn"><Check size={13} /> Save</button>
                    <button onClick={() => setEditingId(null)} className="flex items-center gap-1 text-xs px-2 py-1.5 bg-slate-200 text-slate-700 rounded-md hover:bg-slate-300"><X size={13} /> Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {pkg.thumbnail_path ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/marketplace/${pkg._id}/thumbnail`}
                        alt="thumbnail"
                        className="w-12 h-12 object-cover rounded-lg border border-slate-200 shrink-0"
                        onError={e => { e.target.style.display = "none"; }}
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#eef1f8] border border-[#c0cfe8] flex items-center justify-center shrink-0">
                        <PackageOpen size={20} className="text-[#0c1e41]" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900 text-sm">{pkg.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_COLORS[pkg.status] || "bg-slate-100 text-slate-600"}`}>
                          {pkg.status}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{pkg.description || "No description"}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                        <span>{(pkg.total_contacts || 0).toLocaleString()} contacts</span>
                        <span className="font-semibold text-[#0c1e41]">{pkg.price_credits} credits</span>
                        <span>{pkg.purchases_count || 0} purchases</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => startEdit(pkg)} className="p-1.5 text-slate-500 hover:text-[#0c1e41] hover:bg-slate-100 rounded-lg" data-testid={`edit-package-${pkg._id}`}>
                      <Edit3 size={15} />
                    </button>
                    <button
                      onClick={() => toggleStatus(pkg)}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all border ${
                        pkg.status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"
                      }`}
                      data-testid={`toggle-status-${pkg._id}`}
                    >
                      {pkg.status === "published" ? <><Eye size={13} /> Published</> : <><EyeOff size={13} /> Draft</>}
                    </button>
                    <button onClick={() => deletePackage(pkg._id)} className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid={`delete-package-${pkg._id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
